import React, { useEffect, useMemo, useState } from 'react';
import { Button, Caption1, Checkbox, Dropdown, Field, Input, MessageBar, MessageBarBody, Option, Text, Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, makeStyles, tokens } from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit20Regular, ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { useEnvironment } from '../../contexts/AppContext';
import { BackendsService, initializeTRAPIClient } from '../../services/api';
import type { APIFormat, APIObject, SecurityGroup } from '../../types/trapi';
import { AliasSection } from './AliasSection';
import { OwnershipSection } from './SharedFields';

const useStyles = makeStyles({
  groupCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  errorCaption: {
    color: tokens.colorPaletteRedForeground1,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  focusableButton: {
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusMedium,
    },
  },
});

export interface ApiFormProps {
  mode: 'create' | 'edit';
  initialData?: APIObject;
  onSubmit: (data: Omit<APIObject, 'metadata'>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  existingApiPaths?: string[];
  formId?: string;
  hideActions?: boolean;
  onStatusChange?: (status: { changeCount: number; changes: string[]; issues: string[]; canSubmit: boolean }) => void;
}

type FormManagedIdentity = {
  id: string;
  name: string;
};

type FormData = {
  apiPath: string;
  apiFormat: APIFormat;
  description: string;
  endpoints: string[];
  security: {
    groups: SecurityGroup[];
    identities: FormManagedIdentity[];
  };
  ownership: {
    owner: string;
    team: string;
    contact: string;
  };
};

type FormErrors = Partial<Record<'apiPath' | 'apiFormat' | 'endpoints' | 'securityGroups', string>>;

const API_FORMAT_OPTIONS: { value: APIFormat; label: string }[] = [
  { value: 'OpenAI', label: 'OpenAI' },
  { value: 'VLLM', label: 'VLLM' },
  { value: 'CustomX', label: 'Custom' },
];

const normalizeApiFormat = (val?: string): APIFormat => {
  const v = (val ?? '').trim();
  if (!v) return 'OpenAI';
  if (v.toLowerCase() === 'custom') return 'CustomX';
  return v as APIFormat;
};

// ── Table+Edit sub-component for Security Groups ──
function SecurityGroupsEditor({ groups, onChange }: { groups: SecurityGroup[]; onChange: (g: SecurityGroup[]) => void }) {
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formOwner, setFormOwner] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');

  const clearForm = () => { setFormId(''); setFormName(''); setFormOwner(''); setFormStart(''); setFormEnd(''); setEditIdx(null); setFormOpen(false); };
  const openAdd = () => { clearForm(); setFormOpen(true); };
  const openEdit = (i: number) => {
    const g = groups[i];
    setFormId(g.id); setFormName(g.name || ''); setFormOwner(g.owner || ''); setFormStart(g.startDate || ''); setFormEnd(g.endDate || '');
    setEditIdx(i); setFormOpen(true);
  };
  const remove = (i: number) => { onChange(groups.filter((_, idx) => idx !== i)); if (editIdx === i) clearForm(); };
  const saveItem = () => {
    const id = formId.trim(); if (!id) return;
    const item: SecurityGroup = { id, name: formName.trim(), owner: formOwner.trim() || undefined, startDate: formStart || undefined, endDate: formEnd || undefined };
    if (editIdx !== null) onChange(groups.map((g, i) => i === editIdx ? item : g));
    else onChange([...groups, item]);
    clearForm();
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text weight="semibold">Security Groups</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Caption1>{groups.length} group{groups.length === 1 ? '' : 's'}</Caption1>
          <Button size="small" appearance="primary" icon={<Add24Regular />} type="button" onClick={openAdd} disabled={formOpen}>Add</Button>
        </div>
      </div>
      {groups.length > 0 && (
        <Table size="small" aria-label="Security Groups">
          <TableHeader><TableRow>
            <TableHeaderCell style={{ width: '35%' }}>Name</TableHeaderCell>
            <TableHeaderCell style={{ width: '25%' }}>Owner</TableHeaderCell>
            <TableHeaderCell style={{ width: '15%' }}>Expiry</TableHeaderCell>
            <TableHeaderCell style={{ width: '25%', textAlign: 'right' }}>Actions</TableHeaderCell>
          </TableRow></TableHeader>
          <TableBody>
            {groups.map((g, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Text weight="semibold" size={200} style={{ display: 'block' }}>{g.name || '(unnamed)'}</Text>
                  <Caption1 style={{ fontFamily: 'monospace', color: tokens.colorNeutralForeground3 }} title={g.id}>{g.id ? `${g.id.slice(0, 8)}…` : '-'}</Caption1>
                </TableCell>
                <TableCell><Caption1>{g.owner || '-'}</Caption1></TableCell>
                <TableCell><Caption1>{g.endDate || '-'}</Caption1></TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button size="small" appearance="subtle" icon={<Edit20Regular />} type="button" onClick={() => openEdit(i)} aria-label="Edit group">Edit</Button>
                    <Button size="small" appearance="outline" icon={<Delete24Regular />} type="button"
                      style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
                      onClick={() => remove(i)} aria-label="Delete group">Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {formOpen && (
        <div style={{ display: 'grid', gap: 6, borderTop: `1px solid ${tokens.colorNeutralStroke3}`, paddingTop: 8 }}>
          <Text weight="semibold" size={200}>{editIdx !== null ? 'Edit group' : 'Add group'}</Text>
          <Field label="Group ID" required><Input size="small" value={formId} onChange={(_, d) => setFormId(d.value)} disabled={editIdx !== null} /></Field>
          <Field label="Name"><Input size="small" value={formName} onChange={(_, d) => setFormName(d.value)} /></Field>
          <Field label="Owner"><Input size="small" value={formOwner} onChange={(_, d) => setFormOwner(d.value)} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Start Date"><Input size="small" type="date" value={formStart} onChange={(_, d) => setFormStart(d.value)} /></Field>
            <Field label="End Date"><Input size="small" type="date" value={formEnd} onChange={(_, d) => setFormEnd(d.value)} /></Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            <Button size="small" appearance="secondary" type="button" onClick={clearForm}>Cancel</Button>
            <Button size="small" appearance="primary" type="button" onClick={saveItem} disabled={!formId.trim()}>
              {editIdx !== null ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Table+Edit sub-component for Managed Identities ──
function IdentitiesEditor({ identities }: { identities: FormManagedIdentity[] }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text weight="semibold">Managed Identities</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Caption1>{identities.length} {identities.length === 1 ? 'identity' : 'identities'}</Caption1>
          <Button size="small" appearance="primary" icon={<Add24Regular />} type="button" disabled>Add</Button>
        </div>
      </div>
      <MessageBar intent="info">
        <MessageBarBody>
          Managed identities and owners are read-only here. Use the Manage MSI page to add, update, or remove identities and use Models Access to manage model access.
        </MessageBarBody>
      </MessageBar>
      {identities.length > 0 && (
        <Table size="small" aria-label="Managed Identities">
          <TableHeader><TableRow>
            <TableHeaderCell style={{ width: '70%' }}>Name</TableHeaderCell>
            <TableHeaderCell style={{ width: '30%', textAlign: 'right' }}>Actions</TableHeaderCell>
          </TableRow></TableHeader>
          <TableBody>
            {identities.map((mi, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Text weight="semibold" size={200} style={{ display: 'block' }}>{mi.name || `Identity ${i + 1}`}</Text>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button size="small" appearance="subtle" icon={<Edit20Regular />} type="button" disabled aria-label="Edit identity">Edit</Button>
                    <Button size="small" appearance="outline" icon={<Delete24Regular />} type="button"
                      style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
                      disabled aria-label="Delete identity">Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Collapsible backend checkbox list ──
function BackendCheckboxSection({ selected, backendOptions, onChange }: {
  selected: string[];
  backendOptions: { value: string; label: string }[];
  onChange: (endpoints: string[]) => void;
}) {
  const styles = useStyles();
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return backendOptions;
    return backendOptions.filter(b => b.label.toLowerCase().includes(q));
  }, [backendOptions, filter]);

  const toggle = (name: string) => {
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(Array.from(next));
  };

  return (
    <section style={{ display: 'grid', gap: 4 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none', outline: 'none' }}
        className={styles.focusableButton}
        onClick={() => setOpen(p => !p)}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOpen(p => !p); }}
      >
        {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        <Text weight="semibold">Endpoints (Backends)</Text>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
          {selected.length} selected
        </Caption1>
      </div>
      {open && (
        <>
          <Input
            size="small"
            placeholder="Filter backends…"
            value={filter}
            onChange={(_, d) => setFilter(d.value)}
            style={{ marginTop: 4 }}
          />
          <div style={{
            maxHeight: 240, overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke3}`,
            borderRadius: tokens.borderRadiusMedium, padding: 4, marginTop: 2,
          }}>
            {backendOptions.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No backends available</Caption1>
            ) : filtered.length === 0 ? (
              <Caption1 style={{ padding: 8, display: 'block' }}>No backends match filter</Caption1>
            ) : (
              filtered.map(opt => (
                <Checkbox
                  key={opt.value}
                  checked={selectedSet.has(opt.value)}
                  onChange={() => toggle(opt.value)}
                  label={opt.label}
                  style={{ display: 'flex', width: '100%' }}
                />
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}

export const ApiForm: React.FC<ApiFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  existingApiPaths = [],
  formId,
  hideActions = false,
  onStatusChange,
}) => {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();

  useEffect(() => {
    initializeTRAPIClient({ environment: currentEnvironment });
  }, [currentEnvironment]);

  const [backendOptions, setBackendOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const list = (await BackendsService.getBackends({
          environment: currentEnvironment,
          accessToken: token,
        })) as Array<{ name?: string }> | undefined;

        if (Array.isArray(list)) {
          const options = list
            .map(item => {
              const name = (item?.name ?? '').toString().trim();
              if (!name) return null;
              return { value: name, label: name };
            })
            .filter((opt): opt is { value: string; label: string } => !!opt);
          setBackendOptions(options);
        } else {
          setBackendOptions([]);
        }
      } catch {
        // ignore transient errors; form still usable
      }
    })();
  }, [currentEnvironment]); // eslint-disable-line react-hooks/exhaustive-deps

  const [formData, setFormData] = useState<FormData>({
    apiPath: '',
    apiFormat: 'OpenAI',
    description: '',
    endpoints: [],
    security: { groups: [], identities: [] },
    ownership: { owner: '', team: '', contact: '' },
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!initialData) return;
    setFormData({
      apiPath: initialData.apiPath || '',
      apiFormat: normalizeApiFormat(initialData.apiFormat),
      description: initialData.description || '',
      endpoints: initialData.endpoints || [],
      security: {
        groups: initialData.security?.groups || [],
        identities: (initialData.security?.identities || []).map<FormManagedIdentity>(identity => ({
          id: identity.id ?? '',
          name: identity.name ?? '',
        })),
      },
      ownership: {
        owner: initialData.ownership?.owner || '',
        team: initialData.ownership?.team || '',
        contact: initialData.ownership?.contact || '',
      },
    });
  }, [initialData]);

  // ── Change tracking ──
  const formStatus = useMemo(() => {
    const changes: string[] = [];
    const issues: string[] = [];

    // Required field validation
    if (!formData.apiPath.trim()) issues.push('API Path is required');
    if (!formData.apiFormat) issues.push('API Format is required');

    if (mode === 'create') {
      // For create, every filled field is a "change"
      if (formData.apiPath.trim()) changes.push('API Path');
      if (formData.description.trim()) changes.push('Description');
      if (formData.endpoints.length > 0) changes.push(`${formData.endpoints.length} endpoint(s)`);
      if (formData.security.groups.length > 0) changes.push(`${formData.security.groups.length} security group(s)`);
    } else if (initialData) {
      // Compare each field to initial
      if (formData.apiPath.trim() !== (initialData.apiPath || '')) changes.push('API Path');
      if (formData.apiFormat !== normalizeApiFormat(initialData.apiFormat)) changes.push('API Format');
      if (formData.description.trim() !== (initialData.description || '')) changes.push('Description');

      const initEps = new Set(initialData.endpoints || []);
      const currEps = new Set(formData.endpoints);
      if (initEps.size !== currEps.size || ![...initEps].every(e => currEps.has(e))) changes.push('Endpoints');

      const initOwn = initialData.ownership || {};
      if ((formData.ownership.owner || '') !== (initOwn.owner || '')) changes.push('Owner');
      if ((formData.ownership.team || '') !== (initOwn.team || '')) changes.push('Team');
      if ((formData.ownership.contact || '') !== (initOwn.contact || '')) changes.push('Contact');

      // Groups: compare by id set + field values
      const initGroups = initialData.security?.groups || [];
      const currGroups = formData.security.groups;
      if (JSON.stringify(initGroups.map(g => g.id).sort()) !== JSON.stringify(currGroups.map(g => g.id).sort())) {
        changes.push('Security Groups');
      } else if (JSON.stringify(initGroups) !== JSON.stringify(currGroups)) {
        changes.push('Security Groups (modified)');
      }

    }

    return {
      changeCount: changes.length,
      changes,
      issues,
      canSubmit: mode === 'create' ? issues.length === 0 : (changes.length > 0 && issues.length === 0),
    };
  }, [formData, initialData, mode]);

  // Notify parent of status changes
  useEffect(() => {
    onStatusChange?.(formStatus);
  }, [formStatus, onStatusChange]);

  const validate = () => {
    const e: FormErrors = {};
    if (!formData.apiPath.trim()) e.apiPath = 'API Path is required';
    else {
      const normalized = formData.apiPath.toLowerCase().trim();
      const dup = existingApiPaths.some(
        apiPath => apiPath.toLowerCase() === normalized && apiPath !== initialData?.apiPath,
      );
      if (dup) e.apiPath = 'API Path must be unique';
    }
    if (!formData.apiFormat) e.apiFormat = 'API Format is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    // DR-001: /managemsi is the single identity mutation boundary. The generic API form
    // must NOT transport security.identities; the server preserves stored canonical
    // identities server-side and ignores any incoming identities. Submit only the groups
    // this form actually edits. Identities remain tracked in form state for read-only
    // display (IdentitiesEditor) but are never sent.
    const normalizedSecurity: { groups: SecurityGroup[] } = {
      groups: formData.security.groups,
    };

    const payloadBase = {
      ...formData,
      apiPath: formData.apiPath.toLowerCase().trim(),
      apiFormat: normalizeApiFormat(formData.apiFormat),
      security: normalizedSecurity,
    };

    if (mode === 'edit' && initialData) {
      const metadataToSend =
        initialData.metadata && typeof initialData.metadata === 'object' ? initialData.metadata : {};

      const payload: Record<string, unknown> = {
        ...initialData,
        ...payloadBase,
        id: initialData.id,
        environment: initialData.environment,
        ci: initialData.ci,
        metadata: metadataToSend,
      };

      onSubmit(payload as any);
    } else {
      onSubmit({
        ...payloadBase,
        ci: 'API',
      } as any);
    }
  };

  return (
    <form id={formId} onSubmit={submit} style={{ display: 'grid', gap: 16, width: '100%' }}>
      {/* Basic Information */}
      <section style={{ display: 'grid', gap: 8 }}>
        <Text weight="semibold">Basic Information</Text>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'minmax(240px, 1fr) 320px' }}>
          <Field label="API Path" required validationMessage={errors.apiPath}>
            <Input
              style={{ width: '100%' }}
              value={formData.apiPath}
              onChange={(_, d) => setFormData(p => ({ ...p, apiPath: d.value }))}
              placeholder="/api/v1/example"
            />
          </Field>
          <Field label="API Format" required validationMessage={errors.apiFormat}>
            <Dropdown
              style={{ width: '100%' }}
              value={API_FORMAT_OPTIONS.find(o => o.value === formData.apiFormat)?.label ?? formData.apiFormat}
              selectedOptions={[formData.apiFormat]}
              onOptionSelect={(_, d) => {
                if (d.optionValue) setFormData(p => ({ ...p, apiFormat: d.optionValue as APIFormat }));
              }}
            >
              {API_FORMAT_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
        <Field label="Description">
          <Input
            style={{ width: '100%' }}
            value={formData.description}
            onChange={(_, d) => setFormData(p => ({ ...p, description: d.value }))}
          />
        </Field>
      </section>

      {/* Ownership */}
      <OwnershipSection
        data={formData.ownership}
        onChange={(ownership) => setFormData(p => ({ ...p, ownership }))}
      />

      {/* Endpoints (Backends) — collapsible checkbox list */}
      <BackendCheckboxSection
        selected={formData.endpoints}
        backendOptions={backendOptions}
        onChange={(endpoints) => setFormData(p => ({ ...p, endpoints }))}
      />

      {/* Security Configuration */}
      <section style={{ display: 'grid', gap: 12 }}>
        <Text weight="semibold">Security Configuration</Text>

        {/* Security Groups — table + edit pattern */}
        <SecurityGroupsEditor
          groups={formData.security.groups}
          onChange={(groups) => setFormData(p => ({ ...p, security: { ...p.security, groups } }))}
        />

        {/* Managed Identities — table + edit pattern */}
        <IdentitiesEditor identities={formData.security.identities} />
      </section>

      {/* Aliases */}
      {formData.apiPath && <AliasSection apiPath={formData.apiPath} disabled={isLoading} />}

      {/* Actions */}
      {!hideActions && (
        <div className={styles.formActions}>
          <Button appearance="outline" type="button" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button appearance="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : mode === 'create' ? 'Create API' : 'Update API'}
          </Button>
        </div>
      )}
    </form>
  );
};

export default ApiForm;
